<div
  [draggable]="isDraggable()"
  (dragstart)="dragStarted.emit($event)"
  (dragover)="dragOvered.emit($event)"
  (drop)="dropped.emit($event)"
  (dragend)="dragEnded.emit()"
  [class.opacity-40]="isDragging()"
  [class.border-t-2]="isDragOver() && dragOverPosition() === 'before'"
  [class.border-t-blue-400]="isDragOver() && dragOverPosition() === 'before'"
  [class.border-b-2]="isDragOver() && dragOverPosition() === 'after'"
  [class.border-b-blue-400]="isDragOver() && dragOverPosition() === 'after'"
  class="rounded-lg border border-gray-200 overflow-hidden transition-all">
  <div class="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
    <div class="flex items-center gap-2 text-gray-600">
      @if (isDraggable()) {
        <svg
          class="w-4 h-4 text-gray-300 cursor-grab shrink-0"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true">
          <circle cx="9" cy="7" r="1.5" />
          <circle cx="15" cy="7" r="1.5" />
          <circle cx="9" cy="12" r="1.5" />
          <circle cx="15" cy="12" r="1.5" />
          <circle cx="9" cy="17" r="1.5" />
          <circle cx="15" cy="17" r="1.5" />
        </svg>
      }
      <cmi-provider-type-icon [providerType]="providerType()" />
      <h3 class="font-medium text-gray-700 text-sm font-mono">{{ groupName() }}</h3>
    </div>
    @if (isDeleting()) {
      <div class="flex items-center gap-2">
        <span class="text-xs text-gray-600">{{
          'documentProperties.deleteConfirm' | translate
        }}</span>
        <button
          (click)="delete.emit()"
          class="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
          type="button"
          [title]="'documentProperties.deleteConfirmTitle' | translate">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7" />
          </svg>
        </button>
        <button
          (click)="cancelDelete.emit()"
          class="p-1 text-gray-500 hover:bg-gray-200 rounded transition-colors"
          type="button"
          [title]="'documentProperties.cancelTitle' | translate">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    } @else {
      <div class="flex items-center gap-1">
        @if (isDraggable()) {
          <button
            (click)="moveUp.emit()"
            [disabled]="isFirst()"
            class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-default"
            type="button"
            [title]="'documentProperties.moveUp' | translate">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
            </svg>
          </button>
          <button
            (click)="moveDown.emit()"
            [disabled]="isLast()"
            class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-default"
            type="button"
            [title]="'documentProperties.moveDown' | translate">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-4">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>
        }
        <button
          (click)="edit.emit()"
          class="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          type="button"
          [title]="'documentProperties.edit' | translate">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-4">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
          </svg>
        </button>
        <button
          (click)="confirmDelete.emit()"
          class="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          type="button"
          [title]="'documentProperties.delete' | translate">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            class="size-4">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
          </svg>
        </button>
      </div>
    }
  </div>
</div>
